import React, { useState, useEffect } from "react";
import {
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
} from "@heroicons/react/24/outline";
import { useTheme } from "../contexts/ThemeContext";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "services", label: "Services" },
    { id: "lessons", label: "Lessons" },
    { id: "portfolio", label: "Portfolio" },
    { id: "testimonials", label: "Reviews" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg"
          : "bg-transparent"
      }`}
      data-aos="fade-down"
    >
      <div className="max-w-7xl mx-auto px-4 py-4">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3" data-aos="fade-right">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-blue-500 dark:to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
              <img
                src="/images/logo.png"
                alt="English Janala Logo"
                className="w-8 h-8"
              />
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-2xl font-bold font-display text-gray-800 dark:text-white">
                English
              </span>
              <span className="text-2xl font-bold font-bangla gradient-text">
                জানালা
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div
            className="hidden lg:flex items-center space-x-2"
            data-aos="fade-left"
          >
            {navItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-blue-400 font-medium transition-colors relative group px-4 py-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-gray-800"
                data-aos="fade-down"
                data-aos-delay={index * 100}
              >
                {item.label}
                <span className="absolute -bottom-1 left-4 w-0 h-0.5 bg-indigo-600 dark:bg-blue-400 transition-all group-hover:w-[calc(100%-2rem)]"></span>
              </button>
            ))}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ml-2"
              data-aos="fade-down"
              data-aos-delay="600"
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDark ? (
                <SunIcon className="w-5 h-5 text-yellow-500" />
              ) : (
                <MoonIcon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            <button
              className="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-blue-500 dark:to-cyan-500 text-white px-6 py-3 rounded-full hover:from-indigo-700 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-cyan-600 transition-all transform hover:scale-105 shadow-lg ml-4"
              data-aos="fade-down"
              data-aos-delay="700"
            >
              Book Session
            </button>
          </div>

          {/* Mobile menu button */}
          <div
            className="lg:hidden flex items-center space-x-2"
            data-aos="fade-left"
          >
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDark ? (
                <SunIcon className="w-5 h-5 text-yellow-500" />
              ) : (
                <MoonIcon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-blue-400 transition-colors"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden pb-4 border-t border-gray-200 dark:border-gray-700 glass-effect rounded-2xl p-4 mt-4"
            data-aos="fade-down"
          >
            <div className="flex flex-col space-y-3">
              {navItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-blue-400 hover:bg-indigo-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  data-aos="fade-right"
                  data-aos-delay={index * 50}
                >
                  {item.label}
                </button>
              ))}
              <button
                className="mx-4 mt-2 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-blue-500 dark:to-cyan-500 text-white px-6 py-3 rounded-full hover:from-indigo-700 hover:to-purple-700 dark:hover:from-blue-600 dark:hover:to-cyan-600 transition-all"
                data-aos="fade-up"
                data-aos-delay="350"
              >
                Book Session
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
